"use client";

import { useCallback, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import AddAccountForm from "./AddAccountForm";
import AccountList from "./AccountList";
import AccountDetail from "./AccountDetail";
import type {
  FollowChangeItem,
  RelationshipItem,
  TrackedAccountSummary,
} from "./types";

type Tab = "changelog" | "followers" | "following";

export default function TrackingDashboard() {
  const { data: session, status } = useSession();
  const [accounts, setAccounts] = useState<TrackedAccountSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("changelog");
  const [changelog, setChangelog] = useState<FollowChangeItem[]>([]);
  const [followers, setFollowers] = useState<RelationshipItem[]>([]);
  const [following, setFollowing] = useState<RelationshipItem[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [rescrapeLoading, setRescrapeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoadingAccounts(true);
      const res = await fetch("/api/tracking/accounts");
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data = await res.json();
      const list: TrackedAccountSummary[] = (data.accounts ?? []).map(
        (row: TrackedAccountSummary & { profile?: { avatarUrl?: string } | null }) => ({
          id: row.id,
          instagramUsername: row.instagramUsername,
          planTier: row.planTier,
          status: row.status,
          enabledTasks: row.enabledTasks,
          createdAt: row.createdAt,
          avatarUrl: row.avatarUrl ?? row.profile?.avatarUrl ?? null,
          scrape: row.scrape,
        })
      );
      setAccounts(list);
      setError(null);

      if (!selectedId && list.length > 0) {
        setSelectedId(list[0].id);
      } else if (
        selectedId &&
        !list.some((a: TrackedAccountSummary) => a.id === selectedId)
      ) {
        setSelectedId(list[0]?.id ?? null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load accounts");
    } finally {
      setLoadingAccounts(false);
    }
  }, [selectedId]);

  const fetchDetail = useCallback(async (accountId: string) => {
    try {
      setLoadingDetail(true);
      const [changelogRes, followersRes, followingRes] = await Promise.all([
        fetch(`/api/tracking/accounts/${accountId}/changelog`),
        fetch(`/api/tracking/accounts/${accountId}/relationships?side=followers`),
        fetch(`/api/tracking/accounts/${accountId}/relationships?side=following`),
      ]);

      if (!changelogRes.ok || !followersRes.ok || !followingRes.ok) {
        throw new Error("Failed to load account detail");
      }

      const [changelogData, followersData, followingData] = await Promise.all([
        changelogRes.json(),
        followersRes.json(),
        followingRes.json(),
      ]);

      setChangelog(changelogData.changes ?? []);
      setFollowers(followersData.relationships ?? []);
      setFollowing(followingData.relationships ?? []);
      setDetailError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load detail";
      setDetailError(msg);
      console.error(err);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (status === "authenticated") {
      fetchAccounts();
    } else if (status === "unauthenticated") {
      setLoadingAccounts(false);
    }
  }, [status, fetchAccounts]);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  const selected = accounts.find((a) => a.id === selectedId) ?? null;
  const isScraping = accounts.some((a) => a.scrape.isScraping);

  useEffect(() => {
    if (!isScraping || status !== "authenticated") return;
    const timer = setInterval(() => {
      fetchAccounts();
      if (selectedId) fetchDetail(selectedId);
    }, 4000);
    return () => clearInterval(timer);
  }, [isScraping, status, selectedId, fetchAccounts, fetchDetail]);

  const handleRescrape = async () => {
    if (!selectedId) return;
    setRescrapeLoading(true);
    try {
      const res = await fetch(`/api/tracking/accounts/${selectedId}/rescrape`, {
        method: "POST",
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Re-scrape failed");
      await fetchAccounts();
      await fetchDetail(selectedId);
    } catch (err) {
      setDetailError(err instanceof Error ? err.message : "Re-scrape failed");
    } finally {
      setRescrapeLoading(false);
    }
  };

  const hasEmptyData =
    changelog.length === 0 && followers.length === 0 && following.length === 0;

  if (status === "loading" || loadingAccounts) {
    return <p className="text-sm text-muted">Loading tracking dashboard…</p>;
  }

  if (status === "unauthenticated") {
    return (
      <div className="rounded-2xl border border-line bg-surface p-6 text-center">
        <p className="text-sm text-muted">Sign in to use the tracking dashboard.</p>
        <Link
          href="/api/auth/signin?callbackUrl=/track"
          className="mt-4 inline-block rounded-xl grad-bg px-4 py-2 text-sm font-semibold text-white"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong>Preview lane</strong> - not linked in app nav yet. Mock data only. Worker:{" "}
        <code className="rounded bg-white/80 px-1">npm run worker:run</code>
      </div>

      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      {detailError && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {detailError}
        </p>
      )}

      <AddAccountForm
        onAdded={() => {
          fetchAccounts();
        }}
      />

      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <AccountList
          accounts={accounts}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />

        {selected ? (
          <AccountDetail
            username={selected.instagramUsername}
            accountId={selected.id}
            isScraping={selected.scrape.isScraping}
            hasEmptyData={hasEmptyData && !!selected.scrape.lastSuccessAt}
            onRescrape={handleRescrape}
            rescrapeLoading={rescrapeLoading}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            changelog={changelog}
            followers={followers}
            following={following}
            loading={loadingDetail}
          />
        ) : (
          <div className="rounded-2xl border border-dashed border-line bg-surface/60 p-8 text-center text-sm text-muted">
            Select an account to view changelog and lists.
          </div>
        )}
      </div>

      {session?.user?.email && (
        <p className="text-xs text-muted">Signed in as {session.user.email}</p>
      )}
    </div>
  );
}
